import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import type { VoiceAgentStatus } from "@/hooks/voice-agent/types";

export function useRecording(
  siteId: string | undefined,
  setStatus: (s: VoiceAgentStatus) => void,
  stopSpeaking: () => void,
  sendToAgent: (text: string, isQuickAction?: boolean) => Promise<void>,
  speak: (text: string) => Promise<void>,
  setLastTranscription: (v: string | null) => void,
) {
  const [audioLevel, setAudioLevel] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  // Audio recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  // Transcribe audio using Whisper
  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string | null> => {
    try {
      // Convert blob to base64
      const arrayBuffer = await audioBlob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const { data, error } = await supabase.functions.invoke("voice-transcribe", {
        body: {
          audio: base64,
          mimeType: audioBlob.type || 'audio/webm',
        },
      });

      if (error) {
        // Check if this is a "too short" error (expected when user doesn't speak)
        const errorBody = await error.context?.json?.().catch(() => null);
        if (errorBody?.debug?.tooShort) {
          logger.info("useVoiceAgent", "Transcription too short, prompting user to repeat");
          toast.info("Message trop court", { description: "Décrivez votre demande plus en détail." });
          return null;
        }
        if (errorBody?.debug?.hallucination) {
          logger.info("useVoiceAgent", "Hallucination detected");
          toast.info("Audio non reconnu", { description: "Parlez plus fort et plus près du micro." });
          return null;
        }
        throw error;
      }

      if (data?.error) {
        // Handle specific errors from response body with user-friendly feedback
        if (data?.debug?.tooShort) {
          logger.info("useVoiceAgent", "Transcription too short from response");
          toast.info("Message trop court", { description: "Décrivez votre demande plus en détail." });
          return null;
        }
        if (data?.debug?.hallucination) {
          logger.info("useVoiceAgent", "Hallucination from response");
          toast.info("Audio non reconnu", { description: "Parlez plus fort et plus près du micro." });
          return null;
        }
        throw new Error(data.error);
      }

      return data?.text || null;
    } catch (error) {
      logger.error("useVoiceAgent", "Transcription error", error);
      // Show specific error message to user
      const errorMsg = error instanceof Error ? error.message : "Impossible de transcrire l'audio";
      toast.error("Erreur de transcription", { description: errorMsg });
      return null;
    }
  }, []);

  // Start recording with improved reliability and audio level monitoring
  // externalStartTime allows capturing the true press time before async session init
  const startRecording = useCallback(async (externalStartTime?: number) => {
    if (!siteId) {
      toast.error("Erreur", { description: "Sélectionnez un chantier"});
      return;
    }

    // Stop any ongoing speech
    stopSpeaking();

    try {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        toast.error("Erreur microphone", { description: "L'accès au micro n'est disponible qu'en HTTPS. Ouvrez l'application via une adresse sécurisée."});
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error("Erreur microphone", { description: "Votre navigateur ou cet environnement ne prend pas en charge l'accès au micro."});
        return;
      }
      const isMobileOrTouch = typeof navigator !== 'undefined' && (
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
        ('ontouchstart' in window)
      );
      // On mobile/iOS, strict constraints (sampleRate, etc.) often cause OverconstrainedError or silent recording
      const audioConstraints = isMobileOrTouch
        ? { audio: true }
        : {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 16000,
            },
          };
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      } catch (constraintError) {
        const isOverconstrained = constraintError instanceof DOMException &&
          (constraintError.name === 'OverconstrainedError' || constraintError.name === 'ConstraintNotSatisfiedError');
        if (isOverconstrained) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          throw constraintError;
        }
      }


      streamRef.current = stream;
      setMediaStream(stream); // Reactive state for LiveWaveform
      audioChunksRef.current = [];

      // Use external start time if provided (captures true user press time before session init)
      recordingStartTimeRef.current = externalStartTime || Date.now();

      // Do NOT create AudioContext here - LiveWaveform handles visualization
      // Creating multiple AudioContexts causes "failed to start the device" on iOS/mobile

      // Determine supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';


      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      // Use larger chunks (250ms) for better audio quality
      mediaRecorder.start(250);

      // CRITICAL: Small delay to ensure React propagates mediaStream before status change
      // This prevents LiveWaveform from seeing active=true but externalStream=null
      // which would cause a duplicate getUserMedia() call on iOS, breaking audio
      await new Promise(resolve => setTimeout(resolve, 15));

      setStatus("recording");
      logger.debug("useVoiceAgent", "Recording started with audio level monitoring", { externalStartTime });

    } catch (error) {
      logger.error("useVoiceAgent", "Microphone access denied:", error);
      logger.error("useVoiceAgent", "Recording error", error);
      const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isNotAllowed = error instanceof DOMException && error.name === 'NotAllowedError';
      const isChrome = typeof navigator !== 'undefined' && /Chrome|CriOS/i.test(navigator.userAgent);
      let errorMessage: string;
      if (isNotAllowed) {
        if (isMobile) {
          errorMessage = isChrome
            ? "Autorisez le micro pour cette adresse : menu ⋮ > Paramètres du site > Microphone > Autoriser. Ou réouvrez la page et acceptez quand le navigateur demande."
            : "Appuyez sur le micro puis acceptez l'accès quand le navigateur le demande. Si vous avez refusé : Réglages > Safari > Microphone pour ce site.";
        } else {
          errorMessage = "Autorisez le micro pour cette adresse (icône cadenas ou « i » dans la barre d'adresse > Paramètres du site > Microphone). Staging et prod sont des sites différents : autorisez pour chaque URL.";
        }
      } else {
        errorMessage = error instanceof DOMException ? error.message : "Impossible d'accéder au microphone.";
      }
      toast.error("Erreur microphone", { description: errorMessage});
    }
  }, [siteId, stopSpeaking, setStatus]);

  // Stop recording and process with minimum duration check
  const stopRecording = useCallback(async () => {
    // Audio level monitoring is handled by LiveWaveform component
    // No cleanup needed here

    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") {
      logger.warn("useVoiceAgent", "MediaRecorder not active, ignoring stop");
      return;
    }

    // Check minimum recording duration (300ms - reduced for better UX)
    const recordingDuration = Date.now() - recordingStartTimeRef.current;
    if (recordingDuration < 300) {
      logger.warn("useVoiceAgent", "Recording too short", { duration: recordingDuration });

      // Stop but don't process
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        setMediaStream(null);
      }

      toast.success("Trop court", { description: "Maintenez le bouton plus longtemps",
      });
      setStatus("idle");
      return;
    }

    setStatus("processing");

    return new Promise<void>((resolve) => {
      const mediaRecorder = mediaRecorderRef.current!;

      mediaRecorder.onstop = async () => {
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm'
        });

        logger.debug("useVoiceAgent", "Audio recorded", {
          size: audioBlob.size,
          type: audioBlob.type,
          duration: recordingDuration
        });

        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          setMediaStream(null); // Clear reactive state
        }

        // Check minimum size (more lenient threshold)
        if (audioBlob.size < 500) {
          toast.success("Audio trop court", { description: "Parlez plus longtemps",
          });
          setStatus("idle");
          resolve();
          return;
        }

        // Transcribe
        const transcription = await transcribeAudio(audioBlob);

        if (transcription) {
          // Show transcription immediately for feedback
          setLastTranscription(transcription);
          // Send to agent
          await sendToAgent(transcription);
        } else {
          // No transcription - give user feedback that we didn't hear them
          // Only speak if the audio was long enough (not just noise)
          if (audioBlob.size > 10000) {
            await speak("Je n'ai pas compris. Pouvez-vous répéter ?");
          }
          setStatus("idle");
        }

        resolve();
      };

      mediaRecorder.stop();
    });
  }, [transcribeAudio, sendToAgent, speak, setStatus]);

  // Cleanup helper for unmount
  const cleanupRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setMediaStream(null);
    }
  }, []);

  return {
    audioLevel,
    mediaStream,
    mediaRecorderRef,
    streamRef,
    startRecording,
    stopRecording,
    transcribeAudio,
    cleanupRecording,
  };
}
